import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { useActiveFBOs } from "../../../containers/ActiveFBOContainer";
import { Hangar, Obstacle, ObstacleType } from "../../../types";
import { uuidv4 } from "../../../utils";
import { MetricAwareTextField } from "../../../widgets/MetricAwareTextField/MetricAwareTextField";
import {
  getPolygonBounds,
  getPolygonGeometry,
  Point,
} from "./obstacleUtils";

type Props = {
  open: boolean;
  points: Point[];
  hangar: Hangar;
  onSave: (obstacle: Obstacle) => void;
  onClose: () => void;
};

export const PolygonObstacleDialog: React.FC<Props> = ({
  open,
  points,
  hangar,
  onSave,
  onClose,
}) => {
  const { t } = useTranslation();
  const { activeFBO } = useActiveFBOs();

  const [type, setType] = React.useState<ObstacleType>(
    activeFBO?.subscription === "standard"
      ? ObstacleType.NO_TOW_AREA
      : ObstacleType.OBSTRUCTION
  );
  const [height, setHeight] = React.useState<number | null>(null);

  // Reset form when dialog opens
  React.useEffect(() => {
    if (open) {
      setType(
        activeFBO?.subscription === "standard"
          ? ObstacleType.NO_TOW_AREA
          : ObstacleType.OBSTRUCTION
      );
      setHeight(null);
    }
  }, [open, activeFBO?.subscription]);

  const handleSave = () => {
    const geom = getPolygonGeometry(points);
    const bounds = getPolygonBounds(points);

    const obstacle: Obstacle = {
      id: uuidv4(),
      fbo_id: activeFBO.id,
      hangar_id: hangar.id,
      geom,
      type,
      height,
      // Computed bounding box (for compatibility with existing code)
      x: bounds.x,
      y: bounds.y,
      width: bounds.width,
      depth: bounds.depth,
    };

    onSave(obstacle);
  };

  const unit = activeFBO?.settings?.useMetricSystem ? "m" : "ft";
  const bounds = points.length > 0 ? getPolygonBounds(points) : null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {t("settings.hangarSettings.obstacleToolbar.polygonDialogTitle", "Add Polygon Obstacle")}
      </DialogTitle>
      <DialogContent>
        <Stack direction="column" spacing={2} sx={{ mt: 1 }}>
          {/* Polygon preview info */}
          {bounds && (
            <Box
              sx={{
                p: 2,
                bgcolor: "action.hover",
                borderRadius: 1,
              }}
            >
              <Typography variant="subtitle2" gutterBottom>
                {t("settings.hangarSettings.obstacleToolbar.polygonInfo", "Polygon Info")}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t("settings.hangarSettings.obstacleToolbar.pointCount", "Points")}: {points.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t("settings.hangarSettings.obstacleToolbar.boundingBox", "Bounding Box")}:{" "}
                {bounds.width.toFixed(1)} x {bounds.depth.toFixed(1)} {unit}
              </Typography>
            </Box>
          )}

          {/* Type selector */}
          <TextField
            fullWidth
            size="small"
            select
            required
            label={t("settings.hangarSettings.obstacleToolbar.labels.type", "Type")}
            value={type}
            onChange={(evt) => setType(evt.target.value as ObstacleType)}
          >
            <MenuItem
              value={ObstacleType.OBSTRUCTION}
              disabled={activeFBO?.subscription === "standard"}
            >
              {t("settings.hangarSettings.obstacleToolbar.obstacleTypes.obstruction")}
            </MenuItem>
            <MenuItem value={ObstacleType.NO_TOW_AREA}>
              {t("settings.hangarSettings.obstacleToolbar.obstacleTypes.noTowArea")}
            </MenuItem>
            <MenuItem value={ObstacleType.UNOBSTRUCTIVE}>
              {t("settings.hangarSettings.obstacleToolbar.obstacleTypes.unobstructive")}
            </MenuItem>
            <MenuItem value={ObstacleType.CAUTION}>
              {t("settings.hangarSettings.obstacleToolbar.obstacleTypes.caution")}
            </MenuItem>
          </TextField>

          {/* Height input */}
          <MetricAwareTextField
            unit={activeFBO?.settings?.useMetricSystem ? "meters" : "feet"}
            type="number"
            fullWidth
            inputProps={{
              min: 1,
              max: 100,
              step: 0.1,
            }}
            size="small"
            onChange={(value) => setHeight(value)}
            value={height}
            label={t("settings.hangarSettings.obstacleToolbar.labels.height", { unit })}
          />

          <Typography variant="caption" color="text.secondary">
            {t(
              "settings.hangarSettings.obstacleToolbar.heightHint",
              "Height is optional. Set it if the obstacle has height restrictions for aircraft placement."
            )}
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>
          {t("settings.hangarSettings.obstacleToolbar.buttons.cancel", "Cancel")}
        </Button>
        <div style={{ flex: "1 0 0" }} />
        <Button
          variant="contained"
          color="success"
          onClick={handleSave}
          disabled={points.length < 3}
        >
          {t("settings.hangarSettings.obstacleToolbar.buttons.add", "Add")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
